import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { PaymentMethod } from '@/app/enums';
import { useRedirectToDonationDetail } from '@/app/hooks/useRedirecToDonationDetail';
import { IPaymentResult, StatusCode } from '@/app/trust-pay/trust-pay-api';
import { isResponseError } from '@/app/utils/apiCommonUtils';
import { usePostCreateDonation } from '@/src/api/client';
import { CreateDonationResponse, InitPaymentIntentResponse } from '@/src/api/models';
import { CreateDonationRequest } from '@/src/api/models/CreateDonationRequest';
import { captureException } from '@sentry/nextjs';
import { useTranslation } from 'react-i18next';

export const useHandlePaymentProcessed = ({
  onPaymentProcessError,
  formValues,
  transformedValuesForDonationRequest,
  mountedPaymentIntentId,
  paymentIntent,
}: {
  onPaymentProcessError: (value: string) => void;
  formValues: DonationFormBaseValues;
  transformedValuesForDonationRequest: (values: DonationFormBaseValues) => CreateDonationRequest;
  mountedPaymentIntentId?: string;
  paymentIntent?: InitPaymentIntentResponse;
}) => {
  const { mutateAsync: createDonationAsync, isPending, error } = usePostCreateDonation();

  const redirectDoDonationDetail = useRedirectToDonationDetail();
  const { t } = useTranslation();

  const handlePaymentProcessed = async (result: IPaymentResult, paymentIntentId?: string) => {
    if (result.status !== StatusCode.Success) {
      // Report unsuccessful payment
      captureException(new Error('Apple pay error: payment result is not success'), {
        extra: { result: JSON.stringify(result), paymentIntentId },
      });
      onPaymentProcessError(t('components.ApplePayButton.ErrorWhileProcessingApplePay'));
      return;
    }

    const data = transformedValuesForDonationRequest(formValues);

    try {
      const response = (await createDonationAsync({
        data: {
          ...data,
          donationIntent: paymentIntentId,
        },
      })) as CreateDonationResponse;

      redirectDoDonationDetail({ id: response.publicId, paymentMethod: PaymentMethod.Card });
    } catch (error) {
      if (!isResponseError(error)) {
        captureException(new Error('Apple pay error: unable to update payment intent'), {
          extra: {
            error,
            paymentResult: result,
            paymentIntentId,
            apiRequest: data,
            paymentIntent,
            mountedPaymentIntentId,
          },
        });
      } else {
        const responseJson = await error.response.json();
        captureException(new Error('Apple pay error: unable to update payment intent'), {
          extra: { jsonError: responseJson, paymentIntentId },
        });
      }

      onPaymentProcessError(t('components.ApplePayButton.ErrorWhilePaymentUpdate'));
    }
  };

  return { handlePaymentProcessed, isCreationDonation: isPending, createDonationError: error };
};
